import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { UserPlus, Users, Crown, Trash2, Copy, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const roleConfig = {
  admin: { label: "Admin", className: "bg-[#FFE4E6] text-[#9F1239] border-[#FECDD3]" },
  manager: { label: "Manager", className: "bg-[#FDF1E4] text-[#9A5B13] border-[#FDE2C0]" },
  collaborator: { label: "Collaborateur", className: "bg-[#F5EFD8] text-[#3A2E1F] border-[#E5D9B8]" },
};

const EMPTY_FORM = { first_name: "", last_name: "", email: "", role: "collaborator" };

export default function TeamPage() {
  const { user } = useAuth();
  const [data, setData] = useState({ members: [], invites: [] });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [tempPwdInfo, setTempPwdInfo] = useState(null); // { email, password }
  const [confirmRemove, setConfirmRemove] = useState(null);

  const load = () => api.get("/workspace/team").then(({ data }) => setData(data));

  useEffect(() => { load(); }, []);

  const onAdd = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/workspace/members", form);
      toast.success("Membre ajouté à l'équipe");
      setTempPwdInfo({ email: data.member.email, password: data.temp_password });
      setForm(EMPTY_FORM);
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur lors de l'ajout");
    } finally {
      setLoading(false);
    }
  };

  const cancelInvite = async (id) => {
    await api.delete(`/workspace/invite/${id}`);
    toast.success("Invitation annulée");
    load();
  };

  const removeMember = async (m) => {
    try {
      await api.delete(`/workspace/members/${m.id}`);
      toast.success(`${m.full_name} retiré de l'équipe`);
      setConfirmRemove(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copié");
  };

  const isAdmin = user?.role === "admin" || user?.role === "manager" || user?.is_super_admin;

  return (
    <div className="p-6 md:p-10 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-10 flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-terracotta font-semibold mb-2">Équipe</div>
          <h1 className="font-display font-extrabold text-3xl md:text-4xl">Espace équipe</h1>
          <p className="text-af-secondary mt-2">Gérez les membres et les rôles de votre espace de travail.</p>
        </div>
        {isAdmin && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-terracotta hover:bg-[#9C7124] text-white rounded-md h-11" data-testid="add-member-btn">
                <UserPlus className="h-4 w-4 mr-2" /> Ajouter un membre
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-af-card border-af">
              <DialogHeader>
                <DialogTitle className="font-display">Ajouter un membre</DialogTitle>
              </DialogHeader>
              <form onSubmit={onAdd} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="add-first-name">Prénom</Label>
                    <Input
                      id="add-first-name" required
                      data-testid="add-first-name-input"
                      value={form.first_name}
                      onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))}
                      className="mt-1.5 border-af focus-visible:ring-terracotta"
                      placeholder="Aminata"
                    />
                  </div>
                  <div>
                    <Label htmlFor="add-last-name">Nom</Label>
                    <Input
                      id="add-last-name" required
                      data-testid="add-last-name-input"
                      value={form.last_name}
                      onChange={(e) => setForm((f) => ({ ...f, last_name: e.target.value }))}
                      className="mt-1.5 border-af focus-visible:ring-terracotta"
                      placeholder="Diallo"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="add-email">Email</Label>
                  <Input
                    id="add-email" type="email" required
                    data-testid="add-email-input"
                    value={form.email}
                    onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                    className="mt-1.5 border-af focus-visible:ring-terracotta"
                    placeholder="aminata@entreprise.com"
                  />
                </div>
                <div>
                  <Label>Rôle</Label>
                  <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
                    <SelectTrigger className="mt-1.5 border-af focus:ring-terracotta" data-testid="add-role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="manager">Manager</SelectItem>
                      <SelectItem value="collaborator">Collaborateur</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <p className="text-xs text-af-muted">
                  Un mot de passe temporaire sera généré et envoyé par email au nouveau membre.
                </p>
                <DialogFooter>
                  <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Annuler</Button>
                  <Button type="submit" disabled={loading} data-testid="add-submit-btn"
                    className="bg-terracotta hover:bg-[#9C7124] text-white rounded-md">
                    {loading ? "Ajout…" : "Ajouter le membre"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <Card className="bg-af-card border-af rounded-md mb-6">
        <div className="p-6 border-b border-af flex items-center gap-3">
          <Users className="h-5 w-5 text-terracotta" />
          <div>
            <h3 className="font-display font-bold">Membres ({data.members.length})</h3>
          </div>
        </div>
        <div className="divide-y divide-[#E7E5E4]">
          {data.members.map((m) => (
            <div key={m.id} className="p-5 flex items-center gap-4" data-testid={`member-${m.id}`}>
              <div className="h-10 w-10 rounded-full bg-af-subtle border border-af flex items-center justify-center text-terracotta font-bold">
                {m.full_name?.[0]?.toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-af flex items-center gap-2">
                  {m.full_name}
                  {m.id === user?.id && <span className="text-xs text-af-muted">(vous)</span>}
                </div>
                <div className="text-sm text-af-muted truncate">{m.email}</div>
              </div>
              <Badge className={`${roleConfig[m.role]?.className} border rounded-sm`}>
                {m.role === "admin" && <Crown className="h-3 w-3 mr-1" />}
                {roleConfig[m.role]?.label || m.role}
              </Badge>
              {isAdmin && m.id !== user?.id && (
                <button
                  onClick={() => setConfirmRemove(m)}
                  data-testid={`remove-member-${m.id}`}
                  className="text-af-muted hover:text-[#9F1239] transition-colors ml-2"
                  aria-label="Retirer ce membre"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      </Card>

      {data.invites.length > 0 && (
        <Card className="bg-af-card border-af rounded-md">
          <div className="p-6 border-b border-af">
            <h3 className="font-display font-bold">Invitations en attente ({data.invites.length})</h3>
          </div>
          <div className="divide-y divide-[#E7E5E4]">
            {data.invites.map((inv) => (
              <div key={inv.id} className="p-5 flex items-center justify-between" data-testid={`invite-${inv.id}`}>
                <div>
                  <div className="font-medium text-af">{inv.email}</div>
                  <div className="text-xs text-af-muted">Rôle : {roleConfig[inv.role]?.label}</div>
                </div>
                {isAdmin && (
                  <button onClick={() => cancelInvite(inv.id)} className="text-af-muted hover:text-[#9F1239]" data-testid={`cancel-invite-${inv.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Temporary password dialog */}
      <Dialog open={!!tempPwdInfo} onOpenChange={(o) => !o && setTempPwdInfo(null)}>
        <DialogContent className="bg-af-card border-af" data-testid="temp-pwd-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-terracotta" />
              Membre ajouté avec succès
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-af-secondary">
            Un email contenant le mot de passe temporaire a été envoyé à <strong>{tempPwdInfo?.email}</strong>.
            Vous pouvez aussi le copier ci-dessous pour le partager manuellement :
          </p>
          <div className="bg-af-subtle border border-af rounded-md p-4 mt-2">
            <div className="text-xs text-af-muted mb-1">Email</div>
            <div className="font-semibold text-af mb-3">{tempPwdInfo?.email}</div>
            <div className="text-xs text-af-muted mb-1">Mot de passe temporaire</div>
            <div className="flex items-center gap-2">
              <code className="font-mono text-base font-bold text-terracotta tracking-wide flex-1 break-all" data-testid="temp-pwd-value">
                {tempPwdInfo?.password}
              </code>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => copyToClipboard(tempPwdInfo.password)}
                data-testid="temp-pwd-copy-btn"
                className="border-af"
              >
                <Copy className="h-3.5 w-3.5 mr-1" /> Copier
              </Button>
            </div>
          </div>
          <p className="text-xs text-af-muted">
            Le nouveau membre devra changer ce mot de passe lors de sa première connexion depuis <em>Paramètres</em>.
          </p>
          <DialogFooter>
            <Button onClick={() => setTempPwdInfo(null)} className="bg-terracotta hover:bg-[#9C7124] text-white rounded-md" data-testid="temp-pwd-close">
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove member confirm */}
      <Dialog open={!!confirmRemove} onOpenChange={(o) => !o && setConfirmRemove(null)}>
        <DialogContent className="bg-af-card border-af" data-testid="confirm-remove-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Retirer ce membre ?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-af-secondary">
            <strong>{confirmRemove?.full_name}</strong> ({confirmRemove?.email}) sera retiré(e) de votre espace et son compte sera supprimé.
            Cette action est irréversible.
          </p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmRemove(null)}>Annuler</Button>
            <Button
              onClick={() => removeMember(confirmRemove)}
              data-testid="confirm-remove-btn"
              className="bg-[#9F1239] hover:bg-[#7F1D2E] text-white rounded-md"
            >
              <Trash2 className="h-4 w-4 mr-2" /> Retirer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
